import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { getAxiosErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import loginBackground from "@/assets/login-background.png";

// Visuel de la page de connexion, servi par le bundle local.
const BG_URL = loginBackground;

export default function Login() {
  const { user, login } = useAuth();
  const { logoUrl, company_name } = useBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(getAxiosErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - branded panel */}
      <div
        className="hidden lg:flex lg:w-1/2 relative items-end p-12 text-white"
        style={{
          backgroundImage: `linear-gradient(120deg, rgba(29,78,216,0.75), rgba(15,23,42,0.95)), url(${BG_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 max-w-md">
          <div className="h-12 w-12 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center mb-6 ring-1 ring-white/20 overflow-hidden">
            <img src={logoUrl} alt={company_name} className="h-8 w-8 object-contain" />
          </div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-white/70 font-semibold">
            {company_name} · Qualité
          </div>
          <h1 className="mt-3 text-4xl font-bold tracking-tight leading-tight font-heading">
            Gérez la qualité avec la précision d'un auditeur ISO.
          </h1>
          <p className="mt-4 text-white/80 leading-relaxed">
            Litiges, actions correctives et suivi qualité
            correctives. Une plateforme unique, conforme ISO 9001, pour vos
            équipes opérationnelles.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-4 text-white/80 text-sm">
            <div>
              <ShieldCheck size={18} strokeWidth={1.5} className="mb-2" />
              ISO 9001<br />audit-ready
            </div>
            <div>
              <span className="block font-bold text-xl text-white mb-1">9</span>
              Modules
            </div>
            <div>
              <span className="block font-bold text-xl text-white mb-1">100%</span>
              Traçabilité
            </div>
          </div>
        </div>
      </div>

      {/* Right - login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md" data-testid="login-form">
          <div className="flex items-center gap-3 lg:hidden mb-6">
            <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center overflow-hidden shadow-sm">
              <img src={logoUrl} alt={company_name} className="h-8 w-8 object-contain" />
            </div>
            <div>
              <div className="font-bold text-slate-900 font-heading">{company_name}</div>
              <div className="text-xs uppercase tracking-[0.12em] text-slate-400 font-semibold">Qualité</div>
            </div>
          </div>

          <div className="text-[11px] tracking-[0.18em] uppercase text-blue-700 font-bold">
            Espace Qualité
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 font-heading">
            Bon retour parmi nous.
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Connectez-vous pour accéder au tableau de bord QHSE.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-1.5 rounded-xl h-11 bg-white border-slate-200 focus-visible:ring-blue-500"
                placeholder="vous@presthyg.fr"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-1.5 rounded-xl h-11 bg-white border-slate-200 focus-visible:ring-blue-500"
                placeholder="••••••••"
              />
            </div>
            {error ? (
              <div
                className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            ) : null}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm hover:shadow-md transition-all"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Connexion…</>
              ) : (
                "Se connecter"
              )}
            </Button>
          </form>

          <div className="mt-8 text-center text-xs text-slate-400">
            Accès réservé aux collaborateurs autorisés.
            <br />
            En cas de perte d'identifiants, contactez votre administrateur.
          </div>
        </div>
      </div>
    </div>
  );
}
